
import { Brain, BookUser, Scale, BarChart3, CheckCircle } from "lucide-react";

export const AdvisoryServicesSection = () => {
  return (
    <section className="py-16 bg-gray-50" aria-labelledby="advisory-services-heading">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium mb-5">Our Approach</span>
          <h2 id="advisory-services-heading" className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900">
            Advisory Services That Transform Chaos Into Growth
          </h2>
          <p className="text-xl text-gray-600">
            Our comprehensive advisory services create clarity, confidence, and a clear path to business success.
          </p>
        </div>
        
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Virtual CISO Services */}
            <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
              <div className="mb-6 w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center">
                <Brain className="h-7 w-7 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Virtual CISO Services</h3>
              <p className="text-gray-600 mb-6">
                Strategic security leadership without the full-time executive cost:
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-800 font-medium">Executive Security Strategy</p>
                    <p className="text-gray-600 text-sm mt-1">
                      Strategic guidance that aligns security initiatives with your specific business goals and risk tolerance.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-800 font-medium">Board & Leadership Communication</p>
                    <p className="text-gray-600 text-sm mt-1">
                      Translate complex security concepts into business terms that drive informed decision-making at the highest levels.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-800 font-medium">Security Program Development</p>
                    <p className="text-gray-600 text-sm mt-1">
                      Build a right-sized security program that protects what matters without unnecessary complexity or cost.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="pt-6 mt-6 border-t border-gray-100">
                <h4 className="text-gray-900 font-semibold mb-3">Real Client Experience:</h4>
                <p className="text-gray-600 italic">
                  "Our vCISO from edgefi gave us the strategic direction we'd been missing. Now security is a business enabler instead of a constant source of stress." 
                  <span className="text-sm not-italic block mt-1">— Melissa T., CEO, Healthcare Software Company</span>
                </p>
              </div>
            </div>
            
            {/* Security Leadership Development */}
            <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
              <div className="mb-6 w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center">
                <BookUser className="h-7 w-7 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Security Leadership Development</h3>
              <p className="text-gray-600 mb-6">
                We build internal security leadership capabilities that drive long-term success:
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-800 font-medium">Security Steering Committee</p>
                    <p className="text-gray-600 text-sm mt-1">
                      Establish cross-functional governance that aligns security with business objectives and builds organization-wide ownership.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-800 font-medium">Leadership Training & Workshops</p>
                    <p className="text-gray-600 text-sm mt-1">
                      Equip your executives with the security knowledge needed to make confident business decisions.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-800 font-medium">Incident Response Leadership</p>
                    <p className="text-gray-600 text-sm mt-1">
                      Develop decision-making frameworks that enable rapid, effective response during security incidents.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="pt-6 mt-6 border-t border-gray-100">
                <h4 className="text-gray-900 font-semibold mb-3">Real Client Experience:</h4>
                <p className="text-gray-600 italic">
                  "The security steering committee edgefi helped us establish has transformed how we approach security. It's now a business enabler rather than a roadblock." 
                  <span className="text-sm not-italic block mt-1">— Ryan K., COO, Manufacturing Firm</span>
                </p>
              </div>
            </div>
            
            {/* GRC Advisory */}
            <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
              <div className="mb-6 w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center">
                <Scale className="h-7 w-7 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">GRC Advisory & Implementation</h3>
              <p className="text-gray-600 mb-6">
                Transform compliance from a burden into a business advantage:
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-800 font-medium">Unified Compliance Framework</p>
                    <p className="text-gray-600 text-sm mt-1">
                      Streamline multiple regulatory requirements (HIPAA, PCI, SOC 2, CMMC, etc.) into a single efficient control framework.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-800 font-medium">Compliance Readiness & Gap Analysis</p>
                    <p className="text-gray-600 text-sm mt-1">
                      Detailed assessment of your compliance posture with clear, actionable remediation plans that minimize business disruption.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-800 font-medium">Compliance Automation</p>
                    <p className="text-gray-600 text-sm mt-1">
                      Implement solutions that continuously gather compliance evidence, reducing manual effort and improving accuracy.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="pt-6 mt-6 border-t border-gray-100">
                <h4 className="text-gray-900 font-semibold mb-3">Real Client Experience:</h4>
                <p className="text-gray-600 italic">
                  "edgefi helped us achieve SOC 2 compliance in half the time we expected, while simultaneously positioning us to meet our GDPR requirements." 
                  <span className="text-sm not-italic block mt-1">— Jennifer L., CTO, Financial Tech Startup</span>
                </p>
              </div>
            </div>
            
            {/* Strategic Risk Management */}
            <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
              <div className="mb-6 w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center">
                <BarChart3 className="h-7 w-7 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Strategic Risk Management</h3>
              <p className="text-gray-600 mb-6">
                Make informed risk decisions that protect and accelerate your business:
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-800 font-medium">Business-Centric Risk Assessment</p>
                    <p className="text-gray-600 text-sm mt-1">
                      Evaluate security risks in terms of business impact, allowing for informed prioritization of limited resources.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-800 font-medium">Risk Acceptance Framework</p>
                    <p className="text-gray-600 text-sm mt-1">
                      Develop clear criteria for when to accept, transfer, mitigate, or avoid risks based on your business objectives.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-800 font-medium">Security ROI Analysis</p>
                    <p className="text-gray-600 text-sm mt-1">
                      Demonstrate the business value of security investments to drive budget approval and executive support.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="pt-6 mt-6 border-t border-gray-100">
                <h4 className="text-gray-900 font-semibold mb-3">Real Client Experience:</h4>
                <p className="text-gray-600 italic">
                  "The risk framework edgefi implemented helped us focus our limited security budget on the controls that truly matter to our business." 
                  <span className="text-sm not-italic block mt-1">— David P., CFO, Healthcare Provider</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
